import { useEffect, useMemo, useRef } from 'react';

import { ref, remove, set } from 'firebase/database';

import { database } from '@src/firebase/firebase';
import { sendErrorToSentry } from '@src/util/sentry/utils';

const RENEW_MS = 60_000;
const LEASE_TTL_MS = 120_000;

export interface LiveWatchTarget {
  uid: string;
  divisionId?: string;
}

interface UseLiveWatchLeaseParams {
  enabled: boolean;
  organizationId: string;
  targets: LiveWatchTarget[];
  /** Authenticated viewer; when unset no leases are written. */
  viewerUid?: string | null;
}

const leasePath = (targetUid: string, viewerUid: string) =>
  `users/${targetUid}/liveWatch/${viewerUid}`;

const targetIdentity = (target: LiveWatchTarget) =>
  `${target.uid}:${target.divisionId ?? ''}`;

const buildWatchedMap = (
  targets: LiveWatchTarget[],
  viewerUid: string,
): Map<string, LiveWatchTarget> => {
  const next = new Map<string, LiveWatchTarget>();
  targets.forEach(target => {
    if (!target.uid || target.uid === viewerUid) {
      return;
    }
    const key = targetIdentity(target);
    if (!next.has(key)) {
      next.set(key, { uid: target.uid, divisionId: target.divisionId });
    }
  });
  return next;
};

const reportLeaseError = (error: unknown, organizationId: string) => {
  sendErrorToSentry(error, {
    component: 'useLiveWatchLease',
    organizationId,
  });
};

const writeLease = (
  target: LiveWatchTarget,
  organizationId: string,
  viewerUid: string,
): Promise<void> => {
  const expiresAt = Date.now() + LEASE_TTL_MS;
  return set(ref(database, leasePath(target.uid, viewerUid)), {
    expiresAt,
    organizationId,
    ...(target.divisionId ? { divisionId: target.divisionId } : {}),
  }).catch(error => reportLeaseError(error, organizationId));
};

const removeLease = (
  target: LiveWatchTarget,
  organizationId: string,
  viewerUid: string,
): Promise<void> =>
  remove(ref(database, leasePath(target.uid, viewerUid)))
    .catch(error => reportLeaseError(error, organizationId));

/**
 * While the active-users panel is open and the tab is visible, renew a lease
 * per watched user. Closing the panel, hiding the tab, or unmounting removes it.
 *
 * Membership is order-independent and diffed: re-sorting or reordering the list
 * does not remove/rewrite leases that stay in the set.
 */
export const useLiveWatchLease = ({
  enabled,
  organizationId,
  targets,
  viewerUid,
}: UseLiveWatchLeaseParams): void => {
  const targetKey = useMemo(() => {
    if (!viewerUid) {
      return '';
    }
    return Array.from(buildWatchedMap(targets, viewerUid).keys()).sort().join('|');
  }, [targets, viewerUid]);

  const activeRef = useRef(new Map<string, LiveWatchTarget>());
  const stoppedRef = useRef(true);

  // Diff membership when the watched set changes (sorted/deduped key).
  useEffect(() => {
    const effectOrgId = organizationId;
    const effectViewerUid = viewerUid;
    const active = activeRef.current;

    if (!enabled || !effectOrgId || !effectViewerUid || !targetKey) {
      if (active.size > 0 && effectViewerUid) {
        active.forEach(target => {
          void removeLease(target, effectOrgId, effectViewerUid);
        });
        active.clear();
      }
      return undefined;
    }

    const next = buildWatchedMap(targets, effectViewerUid);

    active.forEach((target, key) => {
      if (!next.has(key)) {
        active.delete(key);
        void removeLease(target, effectOrgId, effectViewerUid);
      }
    });

    next.forEach((target, key) => {
      if (!active.has(key)) {
        active.set(key, target);
        if (!stoppedRef.current && !document.hidden) {
          void writeLease(target, effectOrgId, effectViewerUid);
        }
      }
    });

    return undefined;
    // targetKey already captures the watched set identity (order-independent).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, organizationId, viewerUid, targetKey]);

  // Renew + visibility: keep the interval across membership diffs.
  useEffect(() => {
    if (!enabled || !organizationId || !viewerUid) {
      return undefined;
    }

    const effectOrgId = organizationId;
    const effectViewerUid = viewerUid;
    const active = activeRef.current;
    stoppedRef.current = false;

    const writeAll = () => {
      if (stoppedRef.current || document.hidden) {
        return;
      }
      void Promise.all(
        Array.from(active.values()).map(target =>
          writeLease(target, effectOrgId, effectViewerUid),
        ),
      );
    };

    const removeAll = () => {
      void Promise.all(
        Array.from(active.values()).map(target =>
          removeLease(target, effectOrgId, effectViewerUid),
        ),
      );
    };

    const onVisibility = () => {
      if (document.hidden) {
        removeAll();
        return;
      }
      writeAll();
    };

    writeAll();
    const intervalId = window.setInterval(writeAll, RENEW_MS);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stoppedRef.current = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      removeAll();
      active.clear();
    };
  }, [enabled, organizationId, viewerUid]);
};
